import { enablePatches, produceWithPatches, applyPatches, type Patch } from "immer";
import type { EditorState } from "./types";

enablePatches();

const MAX_HISTORY = 50;

export interface HistoryManager {
  add: (
    state: EditorState,
    recipe: (draft: EditorState) => void,
    throttleRate?: number,
  ) => EditorState;
  undo: (state: EditorState) => EditorState | null;
  redo: (state: EditorState) => EditorState | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

interface HistoryEntry {
  patches: Patch[];
  inversePatches: Patch[];
  timestamp: number;
}

export function createHistory(): HistoryManager {
  const undoStack: HistoryEntry[] = [];
  const redoStack: HistoryEntry[] = [];

  return {
    add(state, recipe, throttleRate) {
      const [nextState, patches, inversePatches] = produceWithPatches(
        state,
        recipe,
      );
      if (patches.length > 0) {
        const now = Date.now();
        const shouldThrottleMerge =
          typeof throttleRate === "number" &&
          throttleRate > 0 &&
          undoStack.length > 0 &&
          now - undoStack[undoStack.length - 1].timestamp < throttleRate;

        if (shouldThrottleMerge) {
          const previous = undoStack[undoStack.length - 1];
          previous.patches = [...previous.patches, ...patches];
          previous.inversePatches = [...inversePatches, ...previous.inversePatches];
        } else {
          undoStack.push({ patches, inversePatches, timestamp: now });
        }

        if (undoStack.length > MAX_HISTORY) {
          undoStack.shift();
        }
        redoStack.length = 0;
      }
      return nextState as EditorState;
    },

    undo(state) {
      const entry = undoStack.pop();
      if (!entry) return null;
      redoStack.push(entry);
      return applyPatches(state, entry.inversePatches) as EditorState;
    },

    redo(state) {
      const entry = redoStack.pop();
      if (!entry) return null;
      undoStack.push(entry);
      return applyPatches(state, entry.patches) as EditorState;
    },

    canUndo: () => undoStack.length > 0,
    canRedo: () => redoStack.length > 0,

    clear() {
      undoStack.length = 0;
      redoStack.length = 0;
    },
  };
}
